import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Button,
  Select,
  Input,
  Space,
  Popconfirm,
  Modal,
  Spin,
  Divider,
  Typography,
  Empty,
  Tag,
  message,
} from 'antd';
import { PlusCircleOutlined, DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { getAthenaRouting, updateAthenaRouting } from '../../../actions/athenaRouting';
import { captureAnalytics } from '../../../actions/preload';
import dedupeDocumentCategories from '../../../utils/dedupeDocumentCategories';
import './manage-athena-routing.scss';

const { Option } = Select;
const { Paragraph, Text } = Typography;

const MODE_LABELS = {
  fixed: 'Fixed',
  round_robin: 'Round robin',
};

// Minimum query length before the (4.4k-entry) catalog renders options, and the
// cap on rendered matches — together these keep the typeahead responsive.
const LABEL_SEARCH_MIN_CHARS = 2;
const LABEL_SEARCH_MAX_RESULTS = 50;

// Settings tab for Athena assignee routing. Admins map document categories to a
// set of Athena inbox users (fixed or round-robin) and curate the filing-label
// allowlist users can pick at send time. Config is stored as JSON on the Athena
// credential and saved wholesale.
export class ManageAthenaRouting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      modes: ['fixed', 'round_robin'],
      credentialId: null,
      athenaCredentials: [],
      documentCategories: [],
      // rules: [{ category, mode, assignees: [] }]
      rules: [],
      // labels: [{ documenttypeid, name }] chosen from the global catalog
      labels: [],
      // documentTypeCatalog: static global [{ documenttypeid, name }] from the server
      documentTypeCatalog: [],
      // label picker (typeahead over the catalog)
      labelSearch: '',
      // null (not undefined) so the Select stays controlled and resets to the
      // placeholder after a pick; value={undefined} would flip it to uncontrolled
      // and antd would render the raw id once the matching Option is gone.
      labelPickerValue: null,
      // usernames: curated roster of Athena inbox usernames selectable as assignees
      usernames: [],
      usernameModalOpen: false,
      usernameDraft: '',
    };
    // Lazily-built Set of catalog ids for O(1) "in catalog?" checks.
    this.catalogIdCache = null;
    this.catalogIdCacheRef = null;
    this.componentMounted = true;
    this.loadSequence = 0;
    this.ruleKeySequence = 0;
    // Serialized snapshot of the last loaded/saved state, used to detect whether
    // the form is dirty (so Save can be disabled while pristine).
    this.savedSnapshot = null;
  }

  async componentDidMount() {
    await this.loadRouting();
  }

  componentWillUnmount() {
    this.componentMounted = false;
    this.loadSequence += 1;
  }

  loadRouting = async (credentialId) => {
    this.loadSequence += 1;
    const sequence = this.loadSequence;
    this.setState({ loading: true });
    try {
      const response = await this.props.getAthenaRouting(credentialId);
      if (!this.componentMounted || sequence !== this.loadSequence) return;

      if (response?.error) {
        message.error('Unable to load Athena routing settings');
        return;
      }

      const data = response?.data || {};
      const rules = this.normalizeRules(data.athena_routing);
      const labels = this.normalizeLabels(data.athena_labels);

      // Seed the roster with the union of the server roster and every assignee
      // referenced by loaded rules, so legacy assignees never disappear from the
      // multiselect and the first save persists a complete roster.
      const usernames = this.mergeUsernames(data.athena_usernames || [], this.assigneesFromRules(rules));

      this.savedSnapshot = this.snapshot(rules, labels, usernames);
      this.setState({
        credentialId: data.emr_integration_credential_id || credentialId || null,
        athenaCredentials: data.athena_credentials || [],
        modes: data.modes || ['fixed', 'round_robin'],
        documentCategories: data.document_categories || [],
        documentTypeCatalog: data.document_type_catalog || [],
        rules,
        labels,
        usernames,
        usernameModalOpen: false,
        usernameDraft: '',
      });
    } catch {
      if (this.componentMounted && sequence === this.loadSequence) {
        message.error('Unable to load Athena routing settings');
      }
    } finally {
      if (this.componentMounted && sequence === this.loadSequence) {
        this.setState({ loading: false });
      }
    }
  };

  handleCredentialChange = async (credentialId) => {
    captureAnalytics('athena_routing_credential_changed');
    await this.loadRouting(credentialId);
  };

  // --- Dirty / validation helpers -------------------------------------------

  // Settings JSON predates this UI and may contain polluted entries. Keep the
  // read path tolerant: ignore unusable rows while preserving every valid one.
  normalizeRules = (rulesObject) => {
    if (!rulesObject || typeof rulesObject !== 'object' || Array.isArray(rulesObject)) return [];

    return Object.keys(rulesObject).reduce((rules, category) => {
      const rule = rulesObject[category];
      if (!rule || typeof rule !== 'object' || Array.isArray(rule)) return rules;

      const mode = MODE_LABELS[rule.mode] ? rule.mode : 'fixed';

      rules.push(this.buildRule({
        category,
        mode,
        assignees: this.normalizeRuleAssignees(mode, rule.assignees),
      }));
      return rules;
    }, []);
  };

  normalizeLabels = (labels) => {
    if (!Array.isArray(labels)) return [];

    return labels.filter((label) => label && typeof label === 'object' && !Array.isArray(label));
  };

  snapshot = (rules, labels, usernames) => JSON.stringify({ rules, labels, usernames });

  isDirty = () => {
    if (this.savedSnapshot === null) return false;
    const { rules, labels, usernames } = this.state;
    return this.snapshot(rules, labels, usernames) !== this.savedSnapshot;
  };

  // Every distinct assignee username referenced across the given rules.
  assigneesFromRules = (rules) => rules.reduce((acc, rule) => acc.concat(rule.assignees || []), []);

  // Case-insensitive de-dupe that preserves first-seen casing (mirrors the
  // backend roster sanitizer).
  mergeUsernames = (...lists) => {
    const seen = new Set();
    const result = [];
    lists.forEach((list) => {
      (list || []).forEach((name) => {
        const trimmed = (name || '').toString().trim();
        if (!trimmed) return;
        const key = trimmed.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        result.push(trimmed);
      });
    });
    return result;
  };

  normalizeAssignees = (assignees) => (
    this.mergeUsernames(Array.isArray(assignees) ? assignees : [])
  );

  normalizeRuleAssignees = (mode, assignees) => {
    const normalized = this.normalizeAssignees(assignees);
    return mode === 'fixed' ? normalized.slice(0, 1) : normalized;
  };

  usernameInUse = (name) => {
    const target = name.toLowerCase();
    return this.state.rules.some(
      (rule) => (rule.assignees || []).some((assignee) => assignee.toLowerCase() === target),
    );
  };

  // A rule needs a category and at least one assignee. Incomplete rules are
  // silently dropped on save (payload shape unchanged), so we flag them in the
  // UI instead. Labels are chosen from the catalog, so they're complete by
  // construction — the only invalid case is a legacy id no longer in the catalog
  // (see labelInCatalog).
  isRuleComplete = (rule) => (
    !!rule.category && this.normalizeRuleAssignees(rule.mode, rule.assignees).length > 0
  );

  // O(1) "is this documenttypeid in the global catalog?" backed by a cached Set.
  catalogIdSet = () => {
    const { documentTypeCatalog } = this.state;
    if (!this.catalogIdCache || this.catalogIdCacheRef !== documentTypeCatalog) {
      this.catalogIdCache = new Set(documentTypeCatalog.map((entry) => entry.documenttypeid));
      this.catalogIdCacheRef = documentTypeCatalog;
    }
    return this.catalogIdCache;
  };

  labelInCatalog = (label) => (
    !!label && typeof label === 'object' && this.catalogIdSet().has(label.documenttypeid)
  );

  // --- Rules -----------------------------------------------------------------

  buildRule = (rule) => {
    this.ruleKeySequence += 1;
    return { ...rule, key: `athena-rule-${this.ruleKeySequence}` };
  };

  addRule = () => {
    captureAnalytics('athena_routing_rule_added');
    this.setState((prev) => ({
      rules: [...prev.rules, this.buildRule({ category: undefined, mode: 'fixed', assignees: [] })],
    }));
  };

  updateRule = (index, patch) => {
    captureAnalytics('athena_routing_rule_updated', { fields: Object.keys(patch) });
    this.setState((prev) => ({
      rules: prev.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    }));
  };

  updateRuleMode = (index, mode) => {
    const rule = this.state.rules[index];
    this.updateRule(index, {
      mode,
      assignees: this.normalizeRuleAssignees(mode, rule?.assignees),
    });
  };

  removeRule = (index) => {
    captureAnalytics('athena_routing_rule_removed');
    this.setState((prev) => ({
      rules: prev.rules.filter((_, i) => i !== index),
    }));
  };

  // --- Username roster -------------------------------------------------------

  openUsernameModal = () => {
    captureAnalytics('athena_routing_username_manager_opened');
    this.setState({ usernameModalOpen: true, usernameDraft: '' });
  };

  closeUsernameModal = () => this.setState({ usernameModalOpen: false, usernameDraft: '' });

  addUsername = () => {
    const trimmed = (this.state.usernameDraft || '').trim();
    if (!trimmed) {
      message.error('Enter a username');
      return;
    }
    const exists = this.state.usernames.some((name) => name.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      message.error(`"${trimmed}" is already in the roster`);
      return;
    }
    captureAnalytics('athena_routing_username_added');
    this.setState((prev) => ({
      usernames: [...prev.usernames, trimmed],
      usernameDraft: '',
    }));
  };

  removeUsername = (name) => {
    if (this.usernameInUse(name)) {
      message.error(`"${name}" is used by a routing rule — remove it from that rule first`);
      return;
    }
    captureAnalytics('athena_routing_username_removed');
    this.setState((prev) => ({
      usernames: prev.usernames.filter((existing) => existing !== name),
    }));
  };

  // --- Labels (chosen from the global catalog) -------------------------------

  // Case-insensitive name-substring match, plus numeric-id-prefix match; only
  // once the query is long enough, and capped so rendering stays snappy.
  filteredCatalog = () => {
    const { labelSearch, documentTypeCatalog } = this.state;
    const query = (labelSearch || '').trim().toLowerCase();
    if (query.length < LABEL_SEARCH_MIN_CHARS) return [];

    const numeric = /^\d+$/.test(query);
    const results = [];
    for (let i = 0; i < documentTypeCatalog.length && results.length < LABEL_SEARCH_MAX_RESULTS; i += 1) {
      const entry = documentTypeCatalog[i];
      const nameMatch = entry.name.toLowerCase().includes(query);
      const idMatch = numeric && entry.documenttypeid.startsWith(query);
      if (nameMatch || idMatch) results.push(entry);
    }
    return results;
  };

  addLabelFromCatalog = (documenttypeid) => {
    if (!documenttypeid) return;
    const entry = this.state.documentTypeCatalog.find((e) => e.documenttypeid === documenttypeid);
    if (!entry) return;

    const alreadyChosen = this.state.labels.some((label) => label.documenttypeid === documenttypeid);
    if (alreadyChosen) {
      message.info(`"${entry.name}" is already added`);
      this.setState({ labelSearch: '', labelPickerValue: null });
      return;
    }

    captureAnalytics('athena_routing_label_added');
    this.setState((prev) => ({
      labels: [...prev.labels, { documenttypeid: entry.documenttypeid, name: entry.name }],
      labelSearch: '',
      labelPickerValue: null,
    }));
  };

  removeLabel = (index) => {
    captureAnalytics('athena_routing_label_removed');
    this.setState((prev) => ({
      labels: prev.labels.filter((_, i) => i !== index),
    }));
  };

  // --- Save ------------------------------------------------------------------

  handleSave = async () => {
    const { rules, labels } = this.state;

    // Canonicalize before both payload construction and the post-save snapshot.
    // This keeps polluted legacy arrays such as [null] from looking complete in
    // the UI while the server drops them as blank.
    const persistedRules = rules.reduce((completeRules, rule) => {
      const normalizedRule = {
        ...rule,
        assignees: this.normalizeRuleAssignees(rule.mode, rule.assignees),
      };
      if (this.isRuleComplete(normalizedRule)) completeRules.push(normalizedRule);
      return completeRules;
    }, []);

    const athenaRouting = {};
    persistedRules.forEach((rule) => {
      athenaRouting[rule.category] = {
        mode: rule.mode || 'fixed',
        assignees: rule.assignees,
      };
    });

    // Labels are chosen from the catalog, so completed rows are valid; the only
    // droppable case is a legacy id no longer in the catalog. Exclude those from
    // the payload (the server would 422 them) and warn, mirroring incomplete rules.
    const knownLabels = labels.filter((label) => this.labelInCatalog(label));
    const droppedLabels = labels.length - knownLabels.length;
    const athenaLabels = knownLabels.map((label) => ({
      documenttypeid: label.documenttypeid,
      name: label.name,
    }));

    const { usernames, credentialId } = this.state;

    captureAnalytics('athena_routing_settings_save_clicked', {
      rule_count: Object.keys(athenaRouting).length,
      label_count: athenaLabels.length,
      username_count: usernames.length,
    });
    this.setState({ saving: true });
    try {
      const response = await this.props.updateAthenaRouting({
        athena_routing: athenaRouting,
        athena_labels: athenaLabels,
        athena_usernames: usernames,
      }, credentialId);
      if (!this.componentMounted) return;

      if (response?.error) {
        message.error(response?.response?.data?.error || 'Unable to save Athena routing settings');
        return;
      }

      // Surface anything that was silently dropped so it doesn't look saved.
      const droppedRules = rules.length - persistedRules.length;
      if (droppedRules > 0) {
        message.warning(`${droppedRules} incomplete rule${droppedRules === 1 ? '' : 's'} ${droppedRules === 1 ? 'was' : 'were'} not saved`);
      }
      if (droppedLabels > 0) {
        message.warning(`${droppedLabels} unknown label${droppedLabels === 1 ? '' : 's'} ${droppedLabels === 1 ? 'was' : 'were'} not saved`);
      }

      // The API persists only complete rules and catalog labels. Reflect that
      // filtered payload in the form before taking the new clean baseline; keeping
      // dropped rows visible would make the snapshot disagree with the server.
      const persistedLabels = knownLabels;
      this.setState({ rules: persistedRules, labels: persistedLabels });
      this.savedSnapshot = this.snapshot(persistedRules, persistedLabels, usernames);
      message.success('Athena routing settings saved');
    } catch {
      if (this.componentMounted) message.error('Unable to save Athena routing settings');
    } finally {
      if (this.componentMounted) this.setState({ saving: false });
    }
  };

  // --- Render ----------------------------------------------------------------

  ruleColumns = () => {
    const {
      documentCategories, modes, rules, usernames,
    } = this.state;
    const usedCategories = rules.map((rule) => rule.category).filter(Boolean);
    const categoryOptions = dedupeDocumentCategories(
      documentCategories,
      (category) => category && category.value,
    );

    return [
      {
        title: 'Document Category',
        dataIndex: 'category',
        width: '30%',
        render: (value, _row, index) => (
          <Select
            showSearch
            className={value ? undefined : 'athena-cell--invalid'}
            placeholder="Select category"
            style={{ width: '100%' }}
            value={value}
            optionFilterProp="children"
            onChange={(category) => this.updateRule(index, { category })}
          >
            {categoryOptions.map((category) => (
              <Option
                key={category.value}
                value={category.value}
                disabled={category.value !== value && usedCategories.includes(category.value)}
              >
                {category.label}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Mode',
        dataIndex: 'mode',
        width: '18%',
        render: (value, _row, index) => (
          <Select
            style={{ width: '100%' }}
            value={value}
            onChange={(mode) => this.updateRuleMode(index, mode)}
          >
            {modes.map((mode) => (
              <Option key={mode} value={mode}>{MODE_LABELS[mode] || mode}</Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Assignees (Athena usernames)',
        dataIndex: 'assignees',
        width: '42%',
        render: (value, row, index) => (
          <Select
            mode={row.mode === 'round_robin' ? 'multiple' : undefined}
            showSearch
            className={(value && value.length > 0) ? undefined : 'athena-cell--invalid'}
            style={{ width: '100%' }}
            placeholder="Select usernames"
            value={row.mode === 'round_robin' ? value : value?.[0]}
            optionFilterProp="children"
            notFoundContent="No usernames yet — add one with “Add username”"
            onChange={(assignees) => this.updateRule(index, {
              assignees: this.normalizeRuleAssignees(
                row.mode,
                row.mode === 'round_robin' ? assignees : [assignees],
              ),
            })}
          >
            {usernames.map((username) => (
              <Option key={username} value={username}>{username}</Option>
            ))}
          </Select>
        ),
      },
      {
        title: '',
        dataIndex: 'actions',
        width: '10%',
        align: 'right',
        render: (_value, _row, index) => (
          <Popconfirm
            overlayClassName="athena-rule-remove-confirm"
            placement="topRight"
            title="Remove rule?"
            okText="Remove"
            okType="danger"
            onConfirm={() => this.removeRule(index)}
          >
            <Button type="text" danger aria-label="Remove routing rule" icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      },
    ];
  };

  // Compact tag-picker block: chosen labels as closable Tags plus an inline
  // typeahead search. Canonical labels use a neutral tag; a legacy id no longer
  // in the catalog is flagged with a red "Not in catalog" tag (still removable,
  // and excluded from save). The raw documenttypeid is never surfaced.
  renderFilingLabels() {
    const { labels, labelSearch, labelPickerValue } = this.state;

    return (
      <div className="athena-labels">
        {labels.length > 0 && (
          <div className="athena-labels__tags">
            {labels.map((label, index) => {
              const inCatalog = this.labelInCatalog(label);
              return (
                <Tag
                  key={label.documenttypeid}
                  className="athena-labels__tag"
                  color={inCatalog ? 'default' : 'error'}
                  closable
                  onClose={(e) => {
                    e.preventDefault();
                    this.removeLabel(index);
                  }}
                >
                  {label.name || '(unnamed)'}
                  {!inCatalog && ' — not in catalog'}
                </Tag>
              );
            })}
          </div>
        )}
        <Select
          showSearch
          className="athena-label-picker"
          style={{ width: 320 }}
          placeholder="Search to add a label…"
          value={labelPickerValue}
          searchValue={labelSearch}
          filterOption={false}
          defaultActiveFirstOption={false}
          onSearch={(text) => this.setState({ labelSearch: text })}
          onChange={this.addLabelFromCatalog}
          notFoundContent={(
            labelSearch.trim().length < LABEL_SEARCH_MIN_CHARS
              ? 'Type to search document types'
              : 'No matching document types'
          )}
        >
          {this.filteredCatalog().map((entry) => (
            <Option key={entry.documenttypeid} value={entry.documenttypeid}>
              {entry.name}
            </Option>
          ))}
        </Select>
        {labels.length === 0 && (
          <p className="athena-labels__hint">
            No labels yet — search above to add ones senders can tag documents with.
          </p>
        )}
      </div>
    );
  }

  renderUsernameModal() {
    const { usernameModalOpen, usernameDraft, usernames } = this.state;

    return (
      <Modal
        title="Manage usernames"
        visible={usernameModalOpen}
        onCancel={this.closeUsernameModal}
        footer={[
          <Button key="done" type="primary" onClick={this.closeUsernameModal}>Done</Button>,
        ]}
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          These usernames are what senders can be assigned to. They&apos;re saved
          with the rest of your routing config when you click Save changes.
        </Paragraph>
        <div className="athena-roster__add">
          <Input
            placeholder="e.g. rhallman5"
            value={usernameDraft}
            onChange={(e) => this.setState({ usernameDraft: e.target.value })}
            onPressEnter={this.addUsername}
          />
          <Button type="primary" icon={<PlusCircleOutlined />} onClick={this.addUsername}>
            Add
          </Button>
        </div>
        <div className="athena-roster__list">
          {usernames.length === 0 ? (
            <div className="athena-roster__empty">No usernames yet.</div>
          ) : (
            usernames.map((username) => (
              <div className="athena-roster__row" key={username}>
                <span>{username}</span>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => this.removeUsername(username)}
                />
              </div>
            ))
          )}
        </div>
      </Modal>
    );
  }

  render() {
    const {
      loading, saving, rules, credentialId, athenaCredentials,
    } = this.state;

    const dirty = this.isDirty();

    return (
      <Spin spinning={loading}>
        <div style={{ padding: '0 20px', textAlign: 'left' }}>
          {athenaCredentials.length > 1 && (
            <section className="athena-routing__credential">
              <Text strong>Athena practice</Text>
              <Select
                value={credentialId}
                onChange={this.handleCredentialChange}
                disabled={loading || saving || dirty}
                className="athena-routing__credential-select"
              >
                {athenaCredentials.map((credential) => (
                  <Option key={credential.id} value={credential.id}>{credential.name}</Option>
                ))}
              </Select>
              {dirty && <Text type="secondary">Save changes before switching practices.</Text>}
            </section>
          )}
          <section className="athena-routing__section">
            <div className="athena-routing__section-header">
              <div>
                <h2 className="athena-routing__section-title">Assignee Routing</h2>
                <Paragraph type="secondary" className="athena-routing__section-desc">
                  Map each document category to the Athena inbox user(s) it&apos;s
                  assigned to on upload. Round robin rotates across the listed users
                  so work spreads evenly; senders can override at send time.
                </Paragraph>
              </div>
              <Space>
                <Button icon={<UserAddOutlined />} onClick={this.openUsernameModal}>
                  Add username
                </Button>
                <Button type="primary" icon={<PlusCircleOutlined />} onClick={this.addRule}>
                  Add rule
                </Button>
              </Space>
            </div>
            <Table
              rowKey="key"
              dataSource={rules}
              columns={this.ruleColumns()}
              pagination={false}
              size="small"
              bordered
              tableLayout="fixed"
              rowClassName={(row) => (this.isRuleComplete(row) ? '' : 'athena-row--invalid')}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No routing rules yet — add one to auto-assign a category to Athena users."
                  >
                    <Button type="primary" icon={<PlusCircleOutlined />} onClick={this.addRule}>
                      Add rule
                    </Button>
                  </Empty>
                ),
              }}
            />
          </section>

          <Divider />

          <section className="athena-routing__section">
            <div className="athena-routing__section-header">
              <div>
                <h2 className="athena-routing__section-title">Filing Labels</h2>
                <Paragraph type="secondary" className="athena-routing__section-desc">
                  Curate the Athena labels senders can pick in the &quot;Label&quot;
                  dropdown when sending to a chart. Search to add one.
                </Paragraph>
              </div>
            </div>
            {this.renderFilingLabels()}
          </section>

          <Divider />

          <div className="athena-routing__save-bar">
            {dirty && (
              <Text type="secondary">You have unsaved changes</Text>
            )}
            <Button
              type="primary"
              loading={saving}
              disabled={!dirty || saving}
              onClick={this.handleSave}
            >
              Save changes
            </Button>
          </div>
          {this.renderUsernameModal()}
        </div>
      </Spin>
    );
  }
}

ManageAthenaRouting.propTypes = {
  getAthenaRouting: PropTypes.func.isRequired,
  updateAthenaRouting: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  getAthenaRouting,
  updateAthenaRouting,
};

export default connect(null, mapDispatchToProps)(ManageAthenaRouting);
